import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import {
  BufferAttribute,
  type BufferGeometry,
  type Material,
  Matrix4,
  Mesh,
  type Object3D,
} from 'three/webgpu';

/**
 * Merge a model built from many small meshes into one mesh per material for each rigid part,
 * so it draws in a handful of calls instead of dozens.
 *
 * The rigid parts are the root and every node marked `userData.moves` (animated: placed, turned
 * or scaled each frame). A part's meshes, wherever they hang below it short of another moving
 * node, are merged in its own frame and added to it; nodes that move stay where they are in the
 * tree. Run it once the model is built, before anything else holds on to its meshes.
 */
export function bakeMeshes(root: Object3D): void {
  const parts = new Map<Object3D, Map<Material, { geos: BufferGeometry[]; meshes: Mesh[] }>>();
  const walk = (node: Object3D, part: Object3D, toPart: Matrix4) => {
    for (const child of [...node.children]) {
      if (child.userData.moves) {
        walk(child, child, new Matrix4());
        continue;
      }
      child.updateMatrix();
      const m = new Matrix4().multiplyMatrices(toPart, child.matrix);
      const mesh = child as Mesh;
      if (mesh.isMesh && !Array.isArray(mesh.material)) {
        let byMat = parts.get(part);
        if (!byMat) {
          byMat = new Map();
          parts.set(part, byMat);
        }
        let set = byMat.get(mesh.material);
        if (!set) {
          set = { geos: [], meshes: [] };
          byMat.set(mesh.material, set);
        }
        set.geos.push(prepare(mesh.geometry, m));
        set.meshes.push(mesh);
      }
      walk(child, part, m);
    }
  };
  walk(root, root, new Matrix4());
  for (const [part, byMat] of parts) {
    for (const [material, { geos, meshes }] of byMat) {
      // A single mesh hanging straight from its part gains nothing.
      if (meshes.length === 1 && meshes[0].parent === part) continue;
      const merged = mergeGeometries(geos);
      if (!merged) continue;
      for (const mesh of meshes) {
        // Its children (if any) are merged too, or move: lift them to the mesh's parent first.
        for (const c of [...mesh.children]) {
          c.applyMatrix4(mesh.matrix);
          mesh.parent?.add(c);
        }
        mesh.removeFromParent();
      }
      const out = new Mesh(merged, material);
      out.frustumCulled = meshes.every((m) => m.frustumCulled);
      part.add(out);
    }
  }
}

/** A copy of `geo` moved by `m`, non-indexed, with exactly position, normal and uv. */
function prepare(geo: BufferGeometry, m: Matrix4): BufferGeometry {
  const g = (geo.index ? geo.toNonIndexed() : geo.clone()).applyMatrix4(m);
  for (const name of Object.keys(g.attributes)) {
    if (name !== 'position' && name !== 'normal' && name !== 'uv') g.deleteAttribute(name);
  }
  if (!g.getAttribute('normal')) g.computeVertexNormals();
  if (!g.getAttribute('uv'))
    g.setAttribute('uv', new BufferAttribute(new Float32Array(g.getAttribute('position').count * 2), 2));
  g.morphAttributes = {};
  g.clearGroups();
  return g;
}
